import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, Link as LinkIcon, FileText, ExternalLink, Download, MoreVertical, Loader2 } from 'lucide-react';
import { cn } from '../utils';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useCommunity } from '../hooks/useCommunity';
import EmptyState from '../components/EmptyState';

const CommunityResources = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { community, loading: communityLoading, isAdmin } = useCommunity(id);
  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const resourcesData = await api.get(`/public/resources?community_id=${id}`);
        setResources(Array.isArray(resourcesData) ? resourcesData : []);
      } catch (err) {
        console.error('Failed to fetch resources', err);
        setResources([]);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [id]);

  const filteredResources = resources.filter(r => 
    r.title?.toLowerCase().includes(search.toLowerCase()) || 
    r.type?.toLowerCase().includes(search.toLowerCase())
  );

  const getResourceColor = (type) => {
    switch(type) {
      case 'PDF': return { color: "text-red-500", bg: "bg-red-50" };
      case 'Link': return { color: "text-brand-indigo", bg: "bg-brand-indigo/5" };
      case 'Sheet': return { color: "text-emerald-500", bg: "bg-emerald-50" };
      case 'Asset': return { color: "text-purple-500", bg: "bg-purple-50" };
      default: return { color: "text-amber-500", bg: "bg-amber-50" };
    }
  };

  if (communityLoading && !community) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
        <Loader2 size={40} className="animate-spin mb-4 text-brand-indigo" />
        <p className="font-bold uppercase tracking-widest text-[10px]">Loading Library...</p>
      </div>
    );
  }

  return (
    <div className="min-w-0 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8 sm:mb-12">
            <div className="text-center md:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">Resource Library</h1>
              <p className="text-sm sm:text-base text-gray-500 font-medium max-w-xl">
                {community ? `Curated knowledge and assets for the ${community.name} community.` : 'Curated knowledge and assets.'}
              </p>
            </div>
            {isAdmin && (
              <button 
                onClick={() => navigate(`/c/${id}/r/new`)}
                className="w-full md:w-auto flex items-center justify-center gap-2 bg-brand-indigo text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-brand-indigo/10 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] text-xs uppercase tracking-widest"
              >
                <Plus size={20} />
                Add Resource
              </button>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 p-2 mb-10 flex items-center shadow-sm focus-within:ring-4 focus-within:ring-brand-indigo/5 transition-all">
             <div className="p-3 text-gray-400">
                <Search size={20} />
             </div>
             <input 
               type="text" 
               value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Search by title, type or extension..."
               className="flex-1 bg-transparent border-none outline-none font-medium py-2 placeholder:text-gray-300"
             />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {loading ? (
              <div className="col-span-full py-20 text-center">
                <Loader2 size={40} className="animate-spin text-brand-indigo mx-auto mb-4" />
                <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest">Scanning archives...</p>
              </div>
            ) : filteredResources.length > 0 ? (
              filteredResources.map((r, i) => {
                const styles = getResourceColor(r.type);
                return (
                  <div key={i} className="bg-white p-6 rounded-[2.5rem] border border-gray-100 flex items-center justify-between group hover:border-brand-indigo/10 transition-all cursor-pointer shadow-sm hover:shadow-2xl hover:shadow-brand-indigo/5 relative overflow-hidden">
                    <div className="flex items-center gap-5 min-w-0">
                      <div className={cn("w-16 h-16 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 shadow-inner shrink-0", styles.bg)}>
                        {r.type === 'Link' ? <LinkIcon className={styles.color} size={28} /> : <FileText className={styles.color} size={28} />}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-gray-900 mb-1.5 group-hover:text-brand-indigo transition-colors truncate text-lg leading-tight">{r.title}</h3>
                        <div className="flex items-center gap-2.5">
                          <span className={cn("text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded shadow-sm", styles.bg, styles.color)}>{r.type}</span>
                          {r.size && (
                            <>
                              <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{r.size}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <a 
                        href={r.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        onClick={(e) => e.stopPropagation()}
                        className="p-3.5 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      >
                        {r.type === 'Link' ? <ExternalLink size={20} /> : <Download size={20} />}
                      </a>
                      {isAdmin && (
                        <button 
                          onClick={(e) => e.stopPropagation()}
                          className="p-3.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm bg-gray-50/50"
                        >
                          <MoreVertical size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full">
                <EmptyState 
                  icon={FileText}
                  title="Library is empty"
                  description={search ? "No resources match your current search." : "There are no resources or documents shared in this community yet."}
                  action={isAdmin && !search && (
                    <button 
                      onClick={() => navigate(`/c/${id}/r/new`)}
                      className="flex items-center gap-2 bg-brand-indigo text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all"
                    >
                      <Plus size={18} />
                      Add First Resource
                    </button>
                  )}
                />
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default CommunityResources;
